import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserData } from '../services/accessControlService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BBALoader from './BBALoader';

const RoleBasedRoute = ({
  children,
  allowedRoles = [],
  allowedUserTypes = [],
  /** When an internal user is denied by `allowedRoles`, redirect here instead of `/crm`. */
  internalDeniedRedirect = '/crm',
  /** /start app shell: require login; send internal users to /crm, client accounts to /portal */
  startAppShell = false,
}) => {
  const { currentUser, loading: authLoading, authError } = useAuth();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [userType, setUserType] = useState(null); // 'internal' or 'portal'
  const [accountType, setAccountType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDataError, setUserDataError] = useState(null);

  useEffect(() => {
    let timeoutId;
    let isMounted = true;

    const fetchUserData = async () => {
      if (currentUser) {
        try {
          // Safety net only: Firestore reports a real outage itself (~10s "client is offline").
          // On phones the first connection (cellular, long-polling fallback) can take well over
          // 8s, and a short timer bounced signed-in users back to login with valid credentials.
          timeoutId = setTimeout(() => {
            if (isMounted) {
              console.warn('User data fetch timeout - redirecting to login');
              setUserDataError(new Error('User data fetch timeout'));
              setLoading(false);
            }
          }, 30000);

          const userData = await getUserData(currentUser.uid);
          if (userData) {
            setUserRole(userData.role);
            setUserType(userData.userType);
            
            // If this is a portal user, check their linked account type
            if (userData.userType === 'portal' && userData.data?.accountId) {
              try {
                const accountDoc = await getDoc(doc(db, 'accounts', userData.data.accountId));
                if (accountDoc.exists()) {
                  const accountData = accountDoc.data();
                  setAccountType(accountData.type);
                }
              } catch (error) {
                console.error('Error fetching linked account:', error);
              }
            }
          }
          
          if (isMounted) {
            setUserDataError(null);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          if (isMounted) {
            setUserDataError(error);
            setLoading(false);
          }
        } finally {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        }
      } else if (!authLoading) {
        // If auth is done loading and no user, stop loading
        setLoading(false);
      }
    };

    // Only fetch user data if auth is not loading and we have a current user
    if (!authLoading && currentUser) {
      fetchUserData();
    } else if (!authLoading && !currentUser) {
      // If auth is done loading and no user, stop loading
      setLoading(false);
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [currentUser, authLoading]);

  // If there's an auth error, redirect to login
  if (authError) {
    console.error('Auth error in RoleBasedRoute:', authError);
    return <Navigate to="/login" state={{ from: location, error: 'Authentication failed' }} replace />;
  }

  // Show loading while auth is loading or fetching user data
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BBALoader size={128} />
      </div>
    );
  }

  // If there's an error fetching user data, redirect to login
  if (userDataError) {
    console.error('User data error in RoleBasedRoute:', userDataError);
    return <Navigate to="/login" state={{ from: location, error: 'Failed to load user data' }} replace />;
  }

  // If not authenticated, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (startAppShell) {
    if (userType === 'internal') {
      return <Navigate to="/crm" replace />;
    }
    if (
      userType === 'portal' &&
      (accountType === 'Clients' || accountType === 'clients')
    ) {
      return <Navigate to="/portal" replace />;
    }
  }

  // Check if user type is allowed
  if (allowedUserTypes.length > 0 && !allowedUserTypes.includes(userType)) {
    // Redirect to appropriate dashboard based on user type and account type
    if (userType === 'portal') {
      // Check account type for portal users
      if (accountType === 'Clients' || accountType === 'clients') {
        return <Navigate to="/portal" replace />;
      } else {
        // Lead account - redirect to start
        return <Navigate to="/start" replace />;
      }
    } else if (userType === 'internal') {
      return <Navigate to="/crm" replace />;
    }
  }

  // Check if user role is allowed
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    // Redirect to appropriate dashboard based on user type and account type
    if (userType === 'portal') {
      // Check account type for portal users
      if (accountType === 'Clients' || accountType === 'clients') {
        return <Navigate to="/portal" replace />;
      } else {
        // Lead account - redirect to start
        return <Navigate to="/start" replace />;
      }
    } else if (userType === 'internal') {
      return <Navigate to={internalDeniedRedirect} replace />;
    }
  }

  // If portal user is trying to access /portal route, check if they're a lead account
  if (userType === 'portal' && location.pathname.startsWith('/portal')) {
    // Only allow client accounts to access /portal
    if (accountType !== 'Clients' && accountType !== 'clients') {
      // Lead account trying to access /portal - redirect to /start
      return <Navigate to="/start" replace />;
    }
  }

  return children;
};

export default RoleBasedRoute; 